/**
 * Evidence Clip Card Component (Phase 17.5)
 *
 * Displays a single blurred evidence clip with:
 * - Native video player (muted, server-side blurred)
 * - Step info: step number, description, safety-critical badge
 * - Score badge using shared score-colors utility
 * - AI reasoning text
 * - Inline feedback: Agree/Disagree toggle, corrected score, rationale
 * - Processing/Failed states for non-completed clips
 */

import { useState, useEffect } from 'react';
import {
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  Clock,
  AlertTriangle,
  Loader2,
  Video,
} from 'lucide-react';
import { getScoreColor } from '../../lib/score-colors';
import type { EvidenceClip, AssessmentFeedbackEntry } from '../../hooks/useAssessments';

// 6-point rubric options for corrected score dropdown
const RUBRIC_OPTIONS = [
  { value: 100, label: 'Exemplary (100)' },
  { value: 80, label: 'Proficient (80)' },
  { value: 60, label: 'Developing (60)' },
  { value: 40, label: 'Struggling (40)' },
  { value: 20, label: 'Needs Training (20)' },
  { value: 0, label: 'Untrained (0)' },
];

function getRubricLabel(score: number): string {
  switch (score) {
    case 100: return 'Exemplary';
    case 80: return 'Proficient';
    case 60: return 'Developing';
    case 40: return 'Struggling';
    case 20: return 'Needs Training';
    case 0: return 'Untrained';
    default: return `${score}`;
  }
}

function formatTimestamp(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type RubricScore = 0 | 20 | 40 | 60 | 80 | 100;

export interface StepFeedbackState {
  stepId: string;
  agreesWithAi: boolean | null;
  correctedScore: RubricScore | undefined;
  rationale: string;
}

interface EvidenceClipCardProps {
  clip: EvidenceClip;
  existingFeedback: AssessmentFeedbackEntry | undefined;
  feedbackState: StepFeedbackState | undefined;
  onFeedbackChange: (state: StepFeedbackState) => void;
}

export function EvidenceClipCard({
  clip,
  existingFeedback,
  feedbackState,
  onFeedbackChange,
}: EvidenceClipCardProps) {
  const step = clip.step;
  const stepId = step?.id ?? clip.id;

  // Initialize local feedback state from existing feedback or empty
  const [localState, setLocalState] = useState<StepFeedbackState>(() => {
    if (feedbackState) return feedbackState;
    if (existingFeedback) {
      return {
        stepId,
        agreesWithAi: existingFeedback.agreesWithAi,
        correctedScore: (existingFeedback.correctedScore as RubricScore | null) ?? undefined,
        rationale: existingFeedback.rationale ?? '',
      };
    }
    return {
      stepId,
      agreesWithAi: null,
      correctedScore: undefined,
      rationale: '',
    };
  });

  // Sync feedbackState prop changes (e.g., after submit resets)
  useEffect(() => {
    if (feedbackState) {
      setLocalState(feedbackState);
    }
  }, [feedbackState]);

  const updateState = (patch: Partial<StepFeedbackState>) => {
    const next = { ...localState, ...patch };
    setLocalState(next);
    onFeedbackChange(next);
  };

  // Queued/Processing state
  if (clip.status === 'queued' || clip.status === 'processing') {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
            <p className="text-sm font-semibold text-gray-700">
              {clip.status === 'queued' ? 'Queued for processing' : 'Processing video clip...'}
            </p>
            {step && (
              <p className="text-xs text-gray-500 mt-1">
                Step {step.stepNumber}: {step.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (clip.status === 'failed') {
    return (
      <div className="bg-white rounded-2xl border border-red-100 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-sm font-semibold text-gray-700">
              Failed to process clip
            </p>
            {clip.errorMessage && (
              <p className="text-xs text-red-600 mt-1 max-w-md">
                {clip.errorMessage}
              </p>
            )}
            {step && (
              <p className="text-xs text-gray-500 mt-2">
                Step {step.stepNumber}: {step.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Completed state — show video and details
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Video Player */}
      {clip.presignedUrl ? (
        <div className="relative bg-black">
          <video
            src={clip.presignedUrl}
            controls
            muted
            crossOrigin="anonymous"
            className="w-full max-h-[400px] object-contain"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50">
          <Video className="w-8 h-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Video not available</p>
        </div>
      )}

      {/* Clip Details */}
      <div className="p-5 space-y-4">
        {/* Step Info Header */}
        {step && (
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Step {step.stepNumber}
                </span>
                {step.isSafetyCritical && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    <ShieldAlert className="w-3 h-3" />
                    Safety Critical
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {step.description}
              </p>
            </div>

            {/* Score Badge */}
            <div className="flex-shrink-0">
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${getScoreColor(step.score, 'badge')}`}
              >
                {step.score} - {getRubricLabel(step.score)}
              </span>
            </div>
          </div>
        )}

        {/* Timestamp Range */}
        {(clip.startTimestamp !== null || clip.endTimestamp !== null) && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>
              {formatTimestamp(clip.startTimestamp)} - {formatTimestamp(clip.endTimestamp)}
            </span>
            {clip.durationSeconds !== null && (
              <span className="text-gray-400">
                ({clip.durationSeconds}s)
              </span>
            )}
          </div>
        )}

        {/* AI Reasoning */}
        {step?.feedback && (
          <div className="p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-100">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              AI Analysis
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {step.feedback}
            </p>
          </div>
        )}

        {/* Inline Feedback Form */}
        {step && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Your Feedback
            </div>

            {/* Agree / Disagree Toggle */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => updateState({ agreesWithAi: true, correctedScore: undefined })}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  localState.agreesWithAi === true
                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
                Agree
              </button>
              <button
                type="button"
                onClick={() => updateState({ agreesWithAi: false })}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  localState.agreesWithAi === false
                    ? 'bg-red-100 text-red-700 ring-1 ring-red-300'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
                Disagree
              </button>
              {existingFeedback && (
                <span className="text-xs text-gray-400 ml-2">
                  (Previously submitted)
                </span>
              )}
            </div>

            {/* Corrected Score Dropdown (only if disagree) */}
            {localState.agreesWithAi === false && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Corrected Score
                </label>
                <select
                  value={localState.correctedScore ?? ''}
                  onChange={(e) =>
                    updateState({
                      correctedScore: e.target.value ? (Number(e.target.value) as RubricScore) : undefined,
                    })
                  }
                  className="w-full max-w-xs px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all"
                >
                  <option value="">Select score...</option>
                  {RUBRIC_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Rationale textarea (shown when agree or disagree is selected) */}
            {localState.agreesWithAi !== null && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {localState.agreesWithAi ? 'Additional notes (optional)' : 'Rationale'}
                </label>
                <textarea
                  value={localState.rationale}
                  onChange={(e) => updateState({ rationale: e.target.value })}
                  placeholder={
                    localState.agreesWithAi
                      ? 'Any additional observations...'
                      : 'Explain why you disagree with the AI scoring...'
                  }
                  rows={2}
                  maxLength={2000}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all resize-none"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EvidenceClipCard;
